import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  WifiIcon, 
  ExclamationTriangleIcon, 
  XMarkIcon,
  SignalIcon,
  SignalSlashIcon
} from '@heroicons/react/24/outline';
import { useNetworkNotification } from '../../hooks/useNetworkStatus';

/**
 * 网络状态通知组件
 */
const NetworkStatusNotification = () => {
  const { showNotification, notificationType, hideNotification } = useNetworkNotification();

  const notificationConfig = {
    offline: {
      icon: SignalSlashIcon,
      title: '网络连接已断开',
      message: '请检查您的网络连接',
      bgColor: 'bg-red-500',
      textColor: 'text-white',
      iconColor: 'text-white'
    },
    slow: {
      icon: SignalIcon,
      title: '网络连接较慢',
      message: '正在为您优化加载体验',
      bgColor: 'bg-yellow-500',
      textColor: 'text-white',
      iconColor: 'text-white'
    }
  };

  const config = notificationConfig[notificationType];

  if (!config) return null;

  const IconComponent = config.icon;

  return (
    <AnimatePresence>
      {showNotification && (
        <motion.div
          initial={{ y: -100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -100, opacity: 0 }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
          className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50"
        >
          <div className={`
            ${config.bgColor} ${config.textColor} 
            px-6 py-4 rounded-lg shadow-lg 
            flex items-center gap-3 min-w-[320px]
            backdrop-blur-sm
          `}>
            <IconComponent className={`w-5 h-5 ${config.iconColor} flex-shrink-0`} />
            
            <div className="flex-1">
              <p className="font-medium text-sm">{config.title}</p>
              <p className="text-xs opacity-90">{config.message}</p>
            </div>

            {notificationType === 'slow' && (
              <button
                onClick={hideNotification}
                className="p-1 hover:bg-white/20 rounded transition-colors"
                aria-label="关闭通知"
              >
                <XMarkIcon className="w-4 h-4" />
              </button>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

/**
 * 网络状态指示器组件（用于状态栏）
 */
export const NetworkStatusIndicator = ({ className = '' }) => {
  const { isOnline, networkQuality, effectiveType } = useNetworkStatus();

  const getStatusConfig = () => {
    if (!isOnline) {
      return {
        icon: SignalSlashIcon,
        color: 'text-red-500',
        label: '离线'
      };
    }

    switch (networkQuality) {
      case 'poor':
        return {
          icon: SignalIcon,
          color: 'text-red-500',
          label: '网络较差'
        };
      case 'moderate':
        return {
          icon: SignalIcon,
          color: 'text-yellow-500',
          label: '网络一般'
        };
      case 'good':
        return {
          icon: WifiIcon,
          color: 'text-green-500',
          label: '网络良好'
        };
      default:
        return {
          icon: WifiIcon,
          color: 'text-gray-500',
          label: '未知'
        };
    }
  };

  const config = getStatusConfig();
  const IconComponent = config.icon;

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <IconComponent className={`w-4 h-4 ${config.color}`} />
      <span className={`text-xs ${config.color}`}>
        {config.label}
        {isOnline && effectiveType && (
          <span className="ml-1 opacity-70">({effectiveType.toUpperCase()})</span>
        )}
      </span>
    </div>
  );
};

/**
 * 网络状态详情组件
 */
export const NetworkStatusDetails = ({ className = '' }) => {
  const { 
    isOnline, 
    connectionType, 
    effectiveType, 
    saveData, 
    rtt, 
    downlink 
  } = useNetworkStatus();

  if (!isOnline) {
    return (
      <div className={`p-4 bg-red-50 border border-red-200 rounded-lg ${className}`}>
        <div className="flex items-center gap-2 text-red-700">
          <SignalSlashIcon className="w-5 h-5" />
          <span className="font-medium">网络连接已断开</span>
        </div>
        <p className="text-red-600 text-sm mt-1">
          请检查您的网络连接后重试
        </p>
      </div>
    );
  }

  return (
    <div className={`p-4 bg-gray-50 border border-gray-200 rounded-lg ${className}`}>
      <div className="flex items-center gap-2 text-gray-700 mb-3">
        <WifiIcon className="w-5 h-5" />
        <span className="font-medium">网络状态</span>
      </div>
      
      <div className="grid grid-cols-2 gap-4 text-sm">
        <div>
          <span className="text-gray-500">连接类型:</span>
          <span className="ml-2 font-medium">{connectionType}</span>
        </div>
        <div>
          <span className="text-gray-500">网络速度:</span>
          <span className="ml-2 font-medium">{effectiveType.toUpperCase()}</span>
        </div>
        {rtt > 0 && (
          <div>
            <span className="text-gray-500">延迟:</span>
            <span className="ml-2 font-medium">{rtt}ms</span>
          </div>
        )}
        {downlink > 0 && (
          <div>
            <span className="text-gray-500">下行速度:</span>
            <span className="ml-2 font-medium">{downlink}Mbps</span>
          </div>
        )}
        <div className="col-span-2">
          <span className="text-gray-500">数据节省:</span>
          <span className={`ml-2 font-medium ${saveData ? 'text-orange-600' : 'text-green-600'}`}>
            {saveData ? '已启用' : '未启用'}
          </span>
        </div>
      </div>
    </div>
  );
};

export default NetworkStatusNotification;
